import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ServiceFieldsSection from './components/ServiceFieldsSection';
import StrengthSection from './components/StrengthSection';
import SolutionCTA from './components/SolutionCTA';

const Solution = () => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'SG SMART GEO 솔루션 | 지반조사·스마트 계측·정밀안전진단·AI 분석',
      url: `${siteUrl}/solution`,
      description:
        'SG SMART GEO의 핵심 솔루션: 지반조사, 스마트 계측, 정밀안전진단, AI 기반 지반 데이터 분석 서비스를 소개합니다.',
      inLanguage: 'ko',
      dateModified: '2026-04-12',
      isPartOf: {
        '@type': 'WebSite',
        name: 'SG SMART GEO',
        url: siteUrl,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '홈',
            item: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '솔루션',
            item: `${siteUrl}/solution`,
          },
        ],
      },
    };

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI 기반 지반 데이터 분석 솔루션',
      provider: {
        '@type': 'Organization',
        name: '주식회사 에스지 SG SMART GEO',
        url: siteUrl,
      },
      serviceType: '지반조사 및 AI 데이터 분석',
      description:
        '지반조사, 스마트 계측, 정밀안전진단, AI 기반 지반 데이터 분석을 통해 안전하고 신뢰할 수 있는 결과를 제공합니다.',
      areaServed: {
        '@type': 'Country',
        name: '대한민국',
      },
      url: `${siteUrl}/solution`,
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'SG SMART GEO 서비스 목록',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '지반조사',
              description: '정밀한 지반조사를 통해 안전하고 신뢰할 수 있는 데이터를 제공합니다.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '스마트 계측',
              description: '첨단 센서와 IoT 기술을 활용한 실시간 스마트 계측 서비스입니다.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '정밀안전진단',
              description: '구조물 및 지반의 정밀안전진단으로 위험 요소를 사전에 파악합니다.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'AI 기반 지반 데이터 분석',
              description: '생성형 AI 기술을 활용한 지반 데이터 분석으로 정확한 의사결정을 지원합니다.',
            },
          },
        ],
      },
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '지반조사는 어떤 현장에서 가능한가요?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '건설 현장, 도로, 교량, 터널, 사면 등 다양한 현장에서 지반조사 서비스를 제공합니다. 현장 특성에 맞는 최적의 조사 방법을 제안해 드립니다.',
          },
        },
        {
          '@type': 'Question',
          name: '스마트 계측 서비스는 어떻게 진행되나요?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'IoT 센서를 현장에 설치하여 지반 변위, 침하, 수위 등을 실시간으로 모니터링합니다. 수집된 데이터는 AI 분석 시스템을 통해 이상 징후를 조기에 감지합니다.',
          },
        },
        {
          '@type': 'Question',
          name: 'AI 기반 지반 데이터 분석이란 무엇인가요?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '생성형 AI 기술을 활용하여 지반조사 및 계측 데이터를 자동으로 분석하고, 위험 예측 및 의사결정을 지원하는 서비스입니다. 기존 수작업 분석 대비 정확도와 속도를 크게 향상시킵니다.',
          },
        },
        {
          '@type': 'Question',
          name: '정밀안전진단은 어떤 절차로 진행되나요?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '현장 조사 → 데이터 수집 → AI 분석 → 보고서 작성 순으로 진행됩니다. 구조물 및 지반의 안전성을 종합적으로 평가하여 위험 요소를 사전에 파악합니다.',
          },
        },
      ],
    };

    const scriptId = 'schema-solution';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify([webPageSchema, serviceSchema, faqSchema]);

    document.title = 'SG SMART GEO 솔루션 | 지반조사·스마트 계측·정밀안전진단·AI 분석';

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
      <Navbar />
      <main>
        <ServiceFieldsSection />
        <StrengthSection />
        <SolutionCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Solution;
